import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { CheckCircle2, Loader2, ArrowRight, CreditCard, LayoutDashboard } from 'lucide-react'

const formatAmount = (amount, currency = 'usd') => {
  const value = Number(amount)
  if (Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value)
}

const BillingSuccess = () => {
  const navigate = useNavigate()
  const { companyUrl } = useAuthStore()

  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let attempts = 0
    let cancelled = false

    // The subscription record updates asynchronously via Stripe webhook, so
    // poll briefly in case it hasn't landed yet by the time we redirect back.
    const poll = async () => {
      const result = await apiService.fetchCurrentSubscription(companyUrl)
      if (cancelled) return

      if (result.success && result.data?.status && result.data.status !== 'inactive') {
        setSubscription(result.data)
        setLoading(false)
        return
      }

      attempts += 1
      if (attempts < 5) {
        setTimeout(poll, 1500)
      } else {
        setLoading(false)
      }
    }

    if (companyUrl) poll()
    return () => { cancelled = true }
  }, [companyUrl])

  return (
    <div className="flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="max-w-md w-full rounded-lg border border-slate-200 bg-white shadow-sm p-8 text-center"
      >
        <motion.div
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 16 }}
          className="w-16 h-16 rounded-lg bg-emerald-100 flex items-center justify-center mx-auto mb-5"
        >
          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        </motion.div>

        <h1 className="text-xl font-semibold text-slate-900 mb-2">Payment successful</h1>
        <p className="text-sm text-slate-500 leading-relaxed">
          Thank you — your subscription is being activated. This page will reflect the confirmed plan shortly.
        </p>

        <div className="mt-6 rounded-lg border border-slate-100 bg-slate-50 p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-slate-500 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Confirming your subscription…</span>
            </div>
          ) : subscription?.plan ? (
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-sm font-semibold text-slate-800">{subscription.plan.name}</p>
                <p className="text-xs text-slate-500">
                  {formatAmount(subscription.plan.amount, subscription.plan.currency)} / {subscription.plan.interval}
                </p>
              </div>
              <span className="text-[11px] font-medium px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                Active
              </span>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              We're still finalizing your subscription. Check the Billing page in a moment for the latest status.
            </p>
          )}
        </div>

        <div className="mt-7 flex flex-col gap-2.5">
          <Button
            onClick={() => navigate('/billing')}
            className="rounded-xl gap-1.5 bg-fitness-primary hover:bg-fitness-primary/90 text-primary-foreground"
          >
            <CreditCard className="h-4 w-4" />
            View Billing
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="rounded-xl gap-1.5"
          >
            <LayoutDashboard className="h-4 w-4" />
            Go to Dashboard
          </Button>
        </div>
      </motion.div>
    </div>
  )
}

export default BillingSuccess
